import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  utils,
  paths,
  t,
  styles,
} from '@hh/global.js'

class ImageVideo extends LitElement {
  static properties = {
    playing: {
      reflect: true,
      type: Boolean,
    },
    firstPlay: {
      attribute: 'first-play',
      reflect: true,
      type: Boolean,
    },
    autoplay: {
      reflect: true,
      type: Boolean,
    },
    volumeEnabled: {
    	mute: false,
    	type: Boolean,
    },
    controls: {
      reflect: true,
      type: Boolean,
    },
		duration: {
			reflect: 0,
			type: Number,
		}
  }

  $play = createRef()
  $pause = createRef()
  $volume = createRef()
  $volume_x = createRef()
  $volume_2 = createRef()

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()

    this.desktopVideo = this.querySelector('[slot="mp4-video"]')
    this.mobileVideo = this.querySelector('[slot="mobile-mp4-video"]')
    this.desktopVideoAlt = this.querySelector('[slot="mp4-video-alt"]')
    this.mobileVideoAlt = this.querySelector('[slot="mobile-mp4-video-alt"]')
    this.youtubeVideo = this.querySelector('[slot="youtube-video"]')

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.mobileScreen = window.matchMedia('(max-width: 749px)')

    this.insideModal = this.dataset.modal;

    if (this.autoplay && (this.desktopVideo || this.desktopVideoAlt || this.youtubeVideo)) {
      const intersectionOptions = {
        rootMargin: '-100px',
      }
      this.intersectionObserver = new IntersectionObserver(
        this.autoplayIntersection.bind(this),
        intersectionOptions
      )
      this.intersectionObserver.observe(this)
    }

    this.addEventListener('first-play', this.handleFirstPlay.bind(this))
  }

  disconnectedCallback() {
    if (this.autoplay && (this.desktopVideo || this.desktopVideoAlt || this.youtubeVideo)) {
      this.intersectionObserver.disconnect()
    }

    this.removeEventListener('first-play', this.handleFirstPlay)

    super.disconnectedCallback()
  }

  updated(changedProperties) {
    if (changedProperties.has('playing')) {
      this.handlePlayingChange(this.playing)
    }
    if (changedProperties.has('volumeEnabled')) {
      this.handleVolumeEnabledChange(this.volumeEnabled)
    }
  }

  /* Templates */
  toggleTemplate() {
    return this.playing
      ? html`
          <!-- @ts-ignore no-incompatible-type-binding -->
          <slot name="pause" @click="${this.toggle}" ${ref(this.$pause)}></slot>
        `
      : html`
          <!-- @ts-ignore no-incompatible-type-binding -->
          <slot name="play" @click="${this.toggle}" ${ref(this.$play)}></slot>
        `
  }

  /* Templates */
  toggleHeadingTemplate() {
    return this.playing
      ? html`
        `
      : html`
          <slot name="heading"></slot>
        `
  }

  toggleVolumeTemplate() {
  	return this.volumeEnabled
	    ? html`
	        <!-- @ts-ignore no-incompatible-type-binding -->
	       	<button class="volume-container">
		        <slot name="volume-2" class="volume-2" @click="${this.toggleVolume}" ${ref(this.$volume_2)}></slot>
						<div class="volume-input-container">
							<br><br>
							<input class="range vertical-heighest-first round" type="range" min="0" max="100" step="1" value="50" @change="${this.adjustVolume}">
							<br><br><br>
						</div>
					</button>
	      `
	    : html`
	        <!-- @ts-ignore no-incompatible-type-binding -->
	        <button class="volume-container">
	        	<slot name="volume-x" @click="${this.toggleVolume}" ${ref(this.$volume_x)}></slot>
	        </button>
	      `
  }

  videoTemplate() {
    if (this.youtubeVideo) {
      return html`
        <div class="youtube-wrapper">
          <slot name="youtube-video"></slot>
        </div>
      `
    } else if (this.desktopVideo) {
      if (this.mobileVideo && this.mobileScreen.matches) {
        this.vdsMedia = this.querySelector('[slot="mobile-mp4-video"]')
        return html`<slot name="mobile-mp4-video"></slot>`
      } else {
        this.vdsMedia = this.querySelector('[slot="mp4-video"]')
        return html`<slot name="mp4-video"></slot>`
      }
    } else if (this.desktopVideoAlt) {
      if (this.mobileVideoAlt && this.mobileScreen.matches) {
        this.videoAlt = this.querySelector('[slot="mobile-mp4-video-alt"]')
        return html`<slot name="mobile-mp4-video-alt"></slot>`
      } else {
        this.videoAlt = this.querySelector('[slot="mp4-video-alt"]')
        return html`<slot name="mp4-video-alt"></slot>`
      }
    }
  }

  /* Methods */
  async toggle() {
    if (!this.playing) {
      this.playing = true
      await this.updateComplete
    } else {
      this.playing = false
      await this.updateComplete
    }
  }

  async toggleVolume() {
    if (!this.volumeEnabled) {
      this.volumeEnabled = true
      await this.updateComplete
    } else {
      this.volumeEnabled = false
      await this.updateComplete
    }
  }

  async adjustVolume(event) {
  	if (this.youtubeVideo) {
			const player = YT.get(this.youtubeVideo.id);
  		player.setVolume(event.target.value);
  	} else if (this.desktopVideo) {
			var event = new CustomEvent("vds-volume-change-request", {'detail': event.target.value / 100});
			this.vdsMedia.dispatchEvent(event);
  	} else if (this.desktopVideoAlt) {
  		this.desktopVideoAlt.querySelector('video').volume = event.target.value / 100;
  	}
  }

  async startVideo() {
    if (this.youtubeVideo) {
      const player = YT.get(this.youtubeVideo.id);
      player.seekTo(0, true);
    } else if (this.desktopVideo) {
    } else if (this.desktopVideoAlt) {
    }
    this.playing = true;
  }

  handlePlayingChange(playing) {
    if (playing) {
    	if (this.youtubeVideo) {
    		this.dispatchVideoEvent('playVideo');
    	} else if (this.desktopVideo) {
    		this.dispatchVideoEvent('vds-play-request');
    	} else if (this.desktopVideoAlt) {
		    if (!this.firstPlay) {
		    	this.dispatchEvent(new CustomEvent('first-play'))
		    }
		    const video = this.desktopVideoAlt.querySelector('video');
		    video.play();
    	}
    } else {
    	if (this.youtubeVideo) {
    		this.dispatchVideoEvent('pauseVideo');
    	} else if (this.desktopVideo) {
    		this.dispatchVideoEvent('vds-pause-request');
    	} else if (this.desktopVideoAlt) {
		    if (!this.firstPlay) {
		    	this.dispatchEvent(new CustomEvent('first-play'))
		    }
    		this.desktopVideoAlt.querySelector('video').pause();
    	}
    }
  }

  handleVolumeEnabledChange(volumeEnabled) {
    if (volumeEnabled) {
    	if (this.youtubeVideo) {
    		const player = YT.get(this.youtubeVideo.id);
    		player.unMute();
    	} else if (this.desktopVideo) {
    		this.dispatchVideoEvent('vds-unmute-request');
    	} else if (this.desktopVideoAlt) {
		    if (!this.firstPlay) {
		    	this.dispatchEvent(new CustomEvent('first-play'))
		    }
    		this.desktopVideoAlt.querySelector('video').muted = false;
    	}
    } else {
    	if (this.youtubeVideo) {
    		const player = YT.get(this.youtubeVideo.id);
    		player.mute();
    	} else if (this.desktopVideo) {
    		this.dispatchVideoEvent('vds-mute-request');
    	} else if (this.desktopVideoAlt) {
		    if (!this.firstPlay) {
		    	this.dispatchEvent(new CustomEvent('first-play'))
		    }
		    this.desktopVideoAlt.querySelector('video').muted = true;
    	}
    }
  }

  handleFirstPlay() {
    this.firstPlay = true
    if (this.mobileScreen.matches) {
      setTimeout(
        () => this.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        0
      )
    }
  }

  autoplayIntersection(entries) {
    if (this.insideModal) {
      if (entries[0].isIntersecting == false) {
        this.playing = false;
      } else {
        this.startVideo();
      }
    } else {
      if (this.reducedMotion.matches || this.mobileScreen.matches) return
      if (entries[0].isIntersecting == false) return
      this.intersectionObserver.disconnect()
      this.playing = true
    }
  }

  dispatchVideoEvent(eventName) {
    if (!this.firstPlay) {
    	this.dispatchEvent(new CustomEvent('first-play'))
    }

    if (this.youtubeVideo) {
      this.youtubeVideo.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: `${eventName}` }),
        'https://www.youtube.com'
      )
    } else if (this.desktopVideo) {
      this.vdsMedia.dispatchEvent(new CustomEvent(eventName))
    }
  }

  render() {
  	if (this.youtubeVideo) {
	    if (this.hasAttribute('controls')) {
		    return html`
		      <div class="container testing">
		        <slot name="media"></slot>
		        ${this.videoTemplate()}
		        <div class="content content--mp4">
		          <div class="content-wrapper">
		            ${this.toggleTemplate()}
		            ${this.toggleHeadingTemplate()}
		          </div>
		        </div>
		      </div>
		    `
	    } else {
		    return html`
		      <div class="container">
		        <slot name="media"></slot>
		        ${this.videoTemplate()}
		        <div class="content">
		          <div class="content-wrapper">
		            <slot name="heading"></slot>
		          </div>
		        </div>
		      </div>
		    `
	    }
	  } else if (this.desktopVideo) {
	  	if (this.hasAttribute('controls')) {
		    return html`
		      <div class="container">
		        <slot name="media"></slot>
		        ${this.videoTemplate()}
		        <div class="content">
		          <div class="content-wrapper">
		            ${this.toggleTemplate()}
		            <slot name="heading"></slot>
		          </div>
		        </div>
		      </div>
		    `
	    } else {
		    return html`
		      <div class="container">
		        <slot name="media"></slot>
		        ${this.videoTemplate()}
		        <div class="content">
		          <div class="content-wrapper">
		            <slot name="heading"></slot>
		          </div>
		        </div>
		      </div>
		    `
	    } 	
  	} else if (this.desktopVideoAlt) {
	    if (this.hasAttribute('controls')) {
		    return html`
		      <div class="container">
		        <slot name="media"></slot>
		        ${this.videoTemplate()}
		        <div class="content content--mp4">
		          <div class="content-wrapper">
		            ${this.toggleTemplate()}
		            ${this.toggleHeadingTemplate()}
		          </div>
		        </div>
		      </div>
		    `
	    } else {
		    return html`
		      <div class="container">
		        <slot name="media"></slot>
		        ${this.videoTemplate()}
		        <div class="content">
		          <div class="content-wrapper">
		            ${this.toggleHeadingTemplate()}
		          </div>
		        </div>
		      </div>
		    `
	    }
  	} else {
        return html`
        <div class="container">
          <slot name="media"></slot>
          ${this.videoTemplate()}
          <div class="content">
            <div class="content-wrapper">
              <slot name="heading"></slot>
            </div>
          </div>
        </div>
      `
    }
  }

  static styles = css`
    :host([first-play]) .content {
      background: none;
    }

    :host ::slotted([slot='mp4-video']),
    :host ::slotted([slot='mobile-mp4-video']),
    :host ::slotted([slot='mp4-video-alt']),
    :host ::slotted([slot='mobile-mp4-video-alt']),
    :host .youtube-wrapper {
      display: none;
      width: 100%;
    }

    :host([first-play]) ::slotted([slot='mp4-video']),
    :host([first-play]) ::slotted([slot='mobile-mp4-video']),
    :host([first-play]) ::slotted([slot='mp4-video-alt']),
    :host([first-play]) ::slotted([slot='mobile-mp4-video-alt']),
    :host([first-play]) .youtube-wrapper,
    :host([playing]) ::slotted([slot='mp4-video']),
    :host([playing]) ::slotted([slot='mobile-mp4-video']),
    :host([playing]) ::slotted([slot='mp4-video-alt']),
    :host([playing]) ::slotted([slot='mobile-mp4-video-alt']),
    :host([playing]) .youtube-wrapper {
      display: block;
    }

    :host([first-play]) ::slotted([slot='media']) {
      display: none;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

   	:host([first-play]) .content--mp4 {
      display: none;
    }

    .container {
      position: relative;
    }

    .content {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: flex-end;
      justify-content: flex-start;
      padding: 1rem;
      background: var(--hh-overlay-gradient);
    }

    @media screen and (min-width: 750px) {
      .content {
        padding: 0.8rem 1.35rem;
      }
    }

    .content-wrapper {
    	width: 100%;
      display: flex;
      align-items: center;
      gap: 1.2rem;
    }

    @media screen and (min-width: 750px) {
      .content-wrapper {
      	min-height: 81px;
      }
    }

    ::slotted([slot='play']) {
    	width: 100%;
    	height: 100%;
    	position: absolute;
    	top: 0;
    	left: 0;
    }

    ::slotted([slot='play']),
    ::slotted([slot='pause']),
    ::slotted([slot='volume']),
   	::slotted([slot='volume-x']),
   	::slotted([slot='volume-2']) {
      border: 0;
      padding: 0;
      color: var(--hh-color-background);
      background-color: transparent;
      cursor: pointer;
    }

    ::slotted([slot='heading']) {
      color: var(--hh-color-background);
      margin: 0;
    }

    :host([first-play]) ::slotted([slot='heading']) {
      display: none;
    }

    .volume-container {
    	display: none;
    }

    @media screen and (max-width: 749px) {
    	:host([first-play]) .volume-container {
    		display: block;
    	}
    }

    @media screen and (min-width: 750px) {
      :host([first-play]) .volume-container {
        display: block;
      }
    }

    .volume-container:focus .volume-input-container {
      display: block;
    }

    @media screen and (min-width: 750px) {
      .volume-container:hover .volume-input-container {
        display: block;
      }
    }

    .youtube-wrapper {
      position: relative;
      padding-bottom: 56.25%;
    }

    .youtube-wrapper ::slotted([slot='youtube-video']) {
      position: absolute;
      width: 100%;
      height: 100%;
      border: 0;
      background-color: transparent;
    }

		.volume-container {
			position: relative;
			background: transparent;
			border: 0;
			outline: 0;
		}

		.volume-input-container {
			display: none;
			position: absolute;
			left: 50%;
			transform: translate(-50%, -50%);
			bottom: 22px;
			padding: 10px;
			max-width: 20%;
		}

		input[type=range].range {
			cursor: pointer;
			-webkit-appearance: none;
			width: 80px !important;
			height: 4px;
			z-index: 200;
		}

		input[type=range].range::-webkit-slider-thumb {
			-webkit-appearance: none;
			width: 12px;
			height: 12px;
			border-radius: 100%;
			background-color: var(--hh-color-background);
		}

		input[type=range].round {
			-webkit-border-radius: 20px;
			-moz-border-radius: 20px;
			border-radius: 20px
		}

		input[type=range].round::-webkit-slider-thumb {
			-webkit-border-radius: 5px;
			-moz-border-radius: 5px;
			-o-border-radius: 5px
		}

		.vertical-heighest-first {
			-webkit-transform: rotate(270deg) translateY(-34px);
			-moz-transform: rotate(270deg) translateY(-34px);
			-o-transform: rotate(270deg) translateY(-34px);
			-ms-transform: rotate(270deg) translateY(-34px);
			transform: rotate(270deg) translateY(-34px)
		}
  `
}

customElements.define('hh-image-video', ImageVideo)
